"use client";

import React, { type ReactNode, useState, useRef, useCallback, ViewTransition } from 'react';
import type { FragmentOfShopifyStorefront } from "@repo/graphql-shopify-storefront";
import { Image, Link, Video } from "@repo/components/atoms";
import { graphqlShopifyStorefront, readFragmentShopifyStorefront } from "@repo/graphql-shopify-storefront";
import { Image_Fragment, MediaImage_Fragment, MediaConnectionMasked_Fragment, Video_Fragment } from "#/graphql/shopify-storefront/fragments";
import { cn, thumbhashDataURL } from "@repo/lib";
import { getMediaQueryForURL, transformShopifyImageToNextImage } from "@repo/shopify";
import { LinkedDataProductGroup } from "#/components/linked-data/product-group";
// import {
//   LinkedData_Localization_Fragment,
//   LinkedData_Product_Fragment,
//   LinkedData_Shop_Fragment,
// } from "#/components/linked-data/graphql";
import { PriceAndCompareAtPrice
// PriceAndCompareAtPrice_Product_Fragment,
} from "#/components/molecules/price-and-compare-at-price";
const initialState = {
  src: undefined,
  pip: false,
  playing: false,
  controls: false,
  light: false,
  volume: 1,
  muted: false,
  played: 0,
  loaded: 0,
  duration: 0,
  playbackRate: 1.0,
  loop: false,
  seeking: false,
  loadedSeconds: 0,
  playedSeconds: 0
};
type PlayerState = Omit<typeof initialState, 'src'> & {
  src?: string;
};
export const GridItem_Localization_Fragment = graphqlShopifyStorefront(`
  fragment GridItem_Localization on Localization {
    ...LinkedData_Localization
    availableCountries {
      availableLanguages {
        isoCode
      }
      isoCode
    }
  }
`
// [LinkedData_Localization_Fragment],
);
export const GridItem_Product_Fragment = graphqlShopifyStorefront(`
  fragment GridItem_Product on Product {
    ...LinkedData_Product
    ...PriceAndCompareAtPrice_Product
    handle
    id
    mediaConnectionMaskedGridItem: media(first: 2) {
      ...MediaConnectionMasked
    }
    priceRange {
      maxVariantPrice {
        amount
        currencyCode
      }
      minVariantPrice {
        amount
        currencyCode
      }
    }
    tags
    title
  }
`
// [
//   MediaConnectionMasked_Fragment,
//   LinkedData_Product_Fragment,
//   PriceAndCompareAtPrice_Product_Fragment,
// ],
);
export const GridItem_Shop_Fragment = graphqlShopifyStorefront(`
  fragment GridItem_Shop on Shop {
    ...LinkedData_Shop
    brand {
      logo {
        alt
        image {
          ...Image
        }
      }
    }
    name
  }
`
// [Image_Fragment, LinkedData_Shop_Fragment],
);
export async function GridItem({
  className,
  delay = 0,
  hide = false,
  lang,
  loading,
  localizationMasked,
  preload,
  productMasked: productMaskedSuper,
  shopMasked,
  sizes
}: {
  className?: string;
  delay?: number;
  hide?: boolean;
  lang: Navigator["language"];
  loading?: "eager" | "lazy";
  localizationMasked: FragmentOfShopifyStorefront<typeof GridItem_Localization_Fragment>;
  preload?: boolean;
  productMasked: FragmentOfShopifyStorefront<typeof GridItem_Product_Fragment>;
  shopMasked: FragmentOfShopifyStorefront<typeof GridItem_Shop_Fragment>;
  sizes?: string[];
}): Promise<ReactNode | null> {
  if (hide) {
    return null;
  }
  const localization = readFragmentShopifyStorefront(GridItem_Localization_Fragment, localizationMasked);
  const product = readFragmentShopifyStorefront(GridItem_Product_Fragment, productMaskedSuper);
  const shop = readFragmentShopifyStorefront(GridItem_Shop_Fragment, shopMasked);
  if (!product) {
    return null;
  }
  const {
    mediaConnectionMaskedGridItem
  } = product;
  if (!product) {
    return null;
  }
  const mediaMasked = readFragmentShopifyStorefront(MediaConnectionMasked_Fragment, mediaConnectionMaskedGridItem).edges.map(({
    node: mediumMasked
  }) => mediumMasked);
  const images = mediaMasked.filter(medium => medium.__typename === "MediaImage").map(mediumMasked_0 => readFragmentShopifyStorefront(MediaImage_Fragment, mediumMasked_0)).map(mediaImage => readFragmentShopifyStorefront(Image_Fragment, mediaImage.imageMasked)).filter(Boolean).map(transformShopifyImageToNextImage);
  const videos = mediaMasked.filter(medium_0 => medium_0.__typename === "Video").map(mediumMasked_1 => readFragmentShopifyStorefront(Video_Fragment, mediumMasked_1));
  const primaryImage = images?.[0];
  const secondaryImage = images?.[1];
  const video = videos?.[1];
  const playerRef = useRef<HTMLVideoElement | null>(null);
  const setPlayerRef = useCallback((player: HTMLVideoElement) => {
    if (!player) return;
    playerRef.current = player;
  }, []);
  const [state, setState] = useState<PlayerState>(initialState);
  const handlePlay = () => {
    if (!video?.sources.length) {
      return;
    }
    setState(prevState => ({
      ...prevState,
      playing: true
    }));
  };
  const handlePause = () => {
    if (!video?.sources.length) {
      return;
    }
    setState(prevState_0 => ({
      ...prevState_0,
      playing: false
    }));
  };
  return <article className={cn("group relative flex h-full w-full flex-col", "focus-visible:ring-offset-white", className)} data-product-id={product.id} data-product-handle={product.handle} data-product-title={product.title} data-product-price={product.priceRange.minVariantPrice?.amount} data-product-currency={product.priceRange.minVariantPrice?.currencyCode} data-product-tags={product.tags.join(", ")} data-product-lang={lang} onMouseEnter={() => handlePlay()} onMouseLeave={() => handlePause()}>
      <Link href={`/products/${product.handle}`}>
        <figure className={cn("mb-0 mt-0 overflow-hidden [contain:inline-size_layout_style]", "aspect-2/3 relative mb-4 w-full overflow-hidden", "group")}>
          <Video className="video absolute inset-0" loop muted playsInline ref={setPlayerRef}>
            {video?.sources.filter(source => source.format !== "m3u8").map(source_0 => <source key={`${source_0.format}-${source_0.url}`} media={getMediaQueryForURL(source_0.url)} src={source_0.url} type={`video/${source_0.format}`} />)}
          </Video>
          {secondaryImage?.src && <ViewTransition name={`${secondaryImage.id}-instance-0`}>
              <Image className={cn("absolute h-full w-full object-cover secondary opacity-0 group-hover:opacity-100 group-focus:opacity-100", className)} alt={secondaryImage.alt ?? product.title} blurDataURL={secondaryImage.thumbhash ? thumbhashDataURL(secondaryImage.thumbhash) : undefined} delay={delay} fill height={undefined} loading="lazy" sizes={sizes?.join(", ")} src={secondaryImage.src} width={undefined} />
            </ViewTransition>}
          {primaryImage?.src && <ViewTransition name={`${primaryImage.id}-instance-0`}>
              <Image className={cn("absolute h-full w-full object-cover primary opacity-100 group-hover:opacity-0 group-focus:opacity-0", className)} alt={primaryImage.alt ?? product.title ?? ""} blurDataURL={primaryImage.thumbhash ? thumbhashDataURL(primaryImage.thumbhash) : undefined} delay={delay} fill height={undefined} loading={loading} preload={preload} sizes={sizes?.join(", ")} src={primaryImage.src} width={undefined} />
            </ViewTransition>}
        </figure>
      </Link>
      {product.priceRange.minVariantPrice ? <footer>
          <ViewTransition name={`${product.id}-title-lg`}>
            <h3 className={cn("mb-2 box-decoration-clone text-xs")}>
              <Link href={`/products/${product.handle}`}>{product.title}</Link>
            </h3>
          </ViewTransition>
          <ViewTransition name={`${product.id}-price-lg`}>
            <PriceAndCompareAtPrice className="text-xs md:grid-flow-col" lang={lang} productMasked={product} />
          </ViewTransition>
        </footer> : null}
      <LinkedDataProductGroup id={"LinkedDataProductGroup"} localizationMasked={localization} productMasked={product} shopMasked={shop} />
    </article>;
}